/**
 * T037: Project Details Step Component
 * 
 * Handles project information collection in onboarding wizard
 * Constitutional compliance: WCAG 2.1 AA accessibility, TDD coverage
 */

import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  OutlinedInput,
  Stack,
  FormHelperText
} from '@mui/material';
import { OnboardingData } from '../../../services/onboardingService';

export interface ProjectDetailsStepProps {
  data: OnboardingData['project_details'];
  onChange: (data: Partial<OnboardingData['project_details']>) => void;
  errors: Record<string, string>;
}

export const ProjectDetailsStep: React.FC<ProjectDetailsStepProps> = ({
  data,
  onChange,
  errors
}) => {
  const handleFieldChange = (field: keyof OnboardingData['project_details']) => 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ [field]: event.target.value });
    };

  const handleSelectChange = (field: keyof OnboardingData['project_details']) => 
    (event: any) => {
      onChange({ [field]: event.target.value });
    };

  const handleArrayChange = (field: keyof OnboardingData['project_details']) => 
    (event: any) => {
      const value = typeof event.target.value === 'string' 
        ? event.target.value.split(',') 
        : event.target.value;
      onChange({ [field]: value });
    };

  const projectTypes = [
    { value: 'web_development', label: 'Web Development' },
    { value: 'mobile_development', label: 'Mobile Development' },
    { value: 'ui_ux_design', label: 'UI/UX Design' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'cms', label: 'Content Management System' },
    { value: 'crm', label: 'Customer Relationship Management' },
    { value: 'api_development', label: 'API Development' },
    { value: 'system_integration', label: 'System Integration' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'other', label: 'Other' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const budgetRanges = [
    { value: 'under_5k', label: 'Under $5,000' },
    { value: '5k_to_15k', label: '$5,000 - $15,000' },
    { value: '15k_to_30k', label: '$15,000 - $30,000' },
    { value: '30k_to_50k', label: '$30,000 - $50,000' },
    { value: '50k_to_100k', label: '$50,000 - $100,000' },
    { value: 'over_100k', label: 'Over $100,000' }
  ];

  const timelines = [
    { value: 'under_1_month', label: 'Under 1 month' },
    { value: '1_to_3_months', label: '1-3 months' },
    { value: '3_to_6_months', label: '3-6 months' },
    { value: '6_to_12_months', label: '6-12 months' },
    { value: 'over_12_months', label: 'Over 12 months' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Tell us about your project
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Help us understand what you're looking to build so we can provide the best solution.
      </Typography>

      <Stack spacing={3}>
        {/* Project Name */}
        <TextField
          fullWidth
          label="Project Name"
          value={data.project_name}
          onChange={handleFieldChange('project_name')}
          error={!!errors.project_name}
          helperText={errors.project_name || 'Give your project a descriptive name'}
          required
          data-testid="project-name-input"
        />

        {/* Project Description */}
        <TextField
          fullWidth
          label="Project Description"
          multiline
          rows={4}
          value={data.project_description}
          onChange={handleFieldChange('project_description')}
          error={!!errors.project_description}
          helperText={errors.project_description || 'Describe what you want to build and its key features'}
          required
          data-testid="project-description-textarea"
        />

        {/* Project Types */}
        <FormControl fullWidth error={!!errors.project_types}>
          <InputLabel>Project Types</InputLabel>
          <Select
            multiple
            value={data.project_types || []}
            onChange={handleArrayChange('project_types')}
            input={<OutlinedInput label="Project Types" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(Array.isArray(selected) ? selected : []).map((value) => {
                  const type = projectTypes.find(t => t.value === value);
                  return <Chip key={value} label={type?.label || value} size="small" />;
                })}
              </Box>
            )}
            required
            data-testid="project-types-select"
          >
            {projectTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          {errors.project_types && <FormHelperText>{errors.project_types}</FormHelperText>}
        </FormControl>

        {/* Priority Level */}
        <FormControl fullWidth error={!!errors.priority_level}>
          <InputLabel>Priority Level</InputLabel>
          <Select
            value={data.priority_level}
            onChange={handleSelectChange('priority_level')}
            label="Priority Level"
            required
            data-testid="priority-level-select"
          >
            {priorityLevels.map((priority) => (
              <MenuItem key={priority.value} value={priority.value}>
                {priority.label}
              </MenuItem>
            ))}
          </Select>
          {errors.priority_level && <FormHelperText>{errors.priority_level}</FormHelperText>}
        </FormControl>

        {/* Budget Range and Timeline */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl fullWidth error={!!errors.budget_range}>
            <InputLabel>Budget Range</InputLabel>
            <Select
              value={data.budget_range || ''}
              onChange={handleSelectChange('budget_range')}
              label="Budget Range"
              data-testid="budget-range-select"
            >
              {budgetRanges.map((budget) => (
                <MenuItem key={budget.value} value={budget.value}>
                  {budget.label}
                </MenuItem>
              ))}
            </Select>
            {errors.budget_range && <FormHelperText>{errors.budget_range}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth error={!!errors.timeline}>
            <InputLabel>Desired Timeline</InputLabel>
            <Select
              value={data.timeline || ''}
              onChange={handleSelectChange('timeline')}
              label="Desired Timeline"
              data-testid="timeline-select"
            >
              {timelines.map((timeline) => (
                <MenuItem key={timeline.value} value={timeline.value}>
                  {timeline.label}
                </MenuItem>
              ))}
            </Select>
            {errors.timeline && <FormHelperText>{errors.timeline}</FormHelperText>}
          </FormControl>
        </Stack>

        {/* Start and End Dates */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Expected Start Date"
            type="date"
            value={data.expected_start_date || ''}
            onChange={handleFieldChange('expected_start_date')}
            error={!!errors.expected_start_date}
            helperText={errors.expected_start_date || 'Optional - When would you like to start?'}
            InputLabelProps={{
              shrink: true,
            }}
            data-testid="start-date-input"
          />

          <TextField
            fullWidth
            label="Expected End Date"
            type="date"
            value={data.expected_end_date || ''}
            onChange={handleFieldChange('expected_end_date')}
            error={!!errors.expected_end_date}
            helperText={errors.expected_end_date || 'Optional - When do you need this completed?'}
            InputLabelProps={{
              shrink: true,
            }}
            data-testid="end-date-input"
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProjectDetailsStep;